import type { PremadeTemplate } from "@/lib/premade-contract-templates";

export const LANDSCAPING_PROPOSAL_TEMPLATES = [
  "Residential Lawn Maintenance",
  "Commercial Grounds Maintenance",
  "Hardscape & Patio Installation",
  "Landscape Design & Installation",
  "Snow & Ice Removal Services",
];

export const LANDSCAPING_CONTRACT_TEMPLATES: PremadeTemplate[] = [
  {
    name: "Landscaping Maintenance Agreement",
    description: "Recurring grounds maintenance contract covering mowing, trimming, fertilization, seasonal cleanups, and irrigation management.",
    content: `LANDSCAPING MAINTENANCE AGREEMENT

This Landscaping Maintenance Agreement ("Agreement") is entered into as of {{date}} by and between:

CONTRACTOR:
{{company_name}}
{{company_address}}
Phone: {{company_phone}} | Email: {{company_email}}
License #: ________________
(hereinafter referred to as "Contractor")

PROPERTY OWNER / CLIENT:
{{client_name}}
{{client_company}}
Email: {{client_email}} | Phone: {{client_phone}}
(hereinafter referred to as "Client")

──────────────────────────────────────────────────

1. PROPERTY DESCRIPTION

Property Address: {{project_address}}
Total Property Area: ________________ sq ft
Property Type: ☐ Residential  ☐ Commercial  ☐ HOA  ☐ Municipal  ☐ Other

──────────────────────────────────────────────────

2. SCOPE OF SERVICES

{{scope_of_work}}

Standard Maintenance Services Include:
• Lawn mowing, edging, and string trimming per schedule
• Mulch application and bed maintenance
• Shrub and hedge pruning and shaping
• Seasonal flower planting and rotation
• Leaf and debris removal
• Fertilization and weed control applications
• Irrigation system monitoring and seasonal adjustments

Excluded from Scope (unless added via Change Order):
• Tree removal or major tree surgery
• Structural hardscape construction
• Pest control and disease treatment
• Major irrigation system repairs or new installations
• Snow and ice removal (unless specified)

──────────────────────────────────────────────────

3. SERVICE SCHEDULE & FREQUENCY

{{estimated_hours}}

Service Season: ________________ through ________________
Service Frequency: ☐ Weekly  ☐ Bi-Weekly  ☐ Monthly  ☐ Seasonal
Service Day(s): ________________
Estimated Crew Size: ________________ crew members

Weather and site conditions may require schedule adjustments. Contractor shall notify Client within twenty-four (24) hours of any schedule changes.

──────────────────────────────────────────────────

4. CONTRACT PRICE & PAYMENT

Total Contract Value: \${{estimated_cost}}
Payment Terms: {{payment_terms}}

Payment Options:
☐ Monthly installments of $________________
☐ Quarterly payments of $________________
☐ Annual payment with ___% discount
☐ Per-visit billing at $________________ per visit

Late payments shall accrue interest at 1.5% per month. Contractor may suspend services if payment is more than fifteen (15) days past due.

──────────────────────────────────────────────────

5. MATERIALS & PLANT WARRANTY

A. All plant materials installed by Contractor carry a ninety (90) day warranty against defects, provided Client maintains proper watering and care.
B. Contractor is not responsible for plant loss due to drought, disease, vandalism, or Acts of God.
C. Replacement materials will be billed at current market rates unless covered under warranty.

──────────────────────────────────────────────────

6. INSURANCE & LIABILITY

Contractor shall maintain:
• Commercial General Liability: $1,000,000 per occurrence / $2,000,000 aggregate
• Workers' Compensation: As required by state law
• Commercial Auto: $1,000,000 combined single limit
• Professional Liability (if applicable): $500,000

Contractor is responsible for any property damage caused by crew negligence during service.

──────────────────────────────────────────────────

7. PROPERTY ACCESS & SAFETY

A. Client shall ensure property access on scheduled service days.
B. Client shall inform Contractor of pets, underground utilities, sprinkler heads, and other hazards.
C. Contractor crew shall wear appropriate PPE and follow all safety protocols.
D. Gates shall be closed and secured after each service visit.

──────────────────────────────────────────────────

8. TERM & RENEWAL

Initial Term: {{contract_length}}
Auto-Renewal: ☐ Yes  ☐ No
Renewal Terms: Agreement automatically renews for successive one-year terms unless either party provides thirty (30) days' written notice of non-renewal.

──────────────────────────────────────────────────

9. TERMINATION

A. Either party may terminate with thirty (30) days' written notice.
B. Client shall pay for all services rendered through the termination date.
C. Early termination fee: ________________ (if applicable).

──────────────────────────────────────────────────

10. ADDITIONAL TERMS

{{terms_and_conditions}}

{{proposal_notes}}

──────────────────────────────────────────────────

11. SIGNATURES

CONTRACTOR:
Name: {{company_name}}
Signature: _________________________ Date: __________

CLIENT:
Name: {{client_name}}
Signature: _________________________ Date: __________
`,
  },
  {
    name: "Hardscape Installation Contract",
    description: "Project-based contract for hardscaping work including patios, retaining walls, walkways, and outdoor living spaces.",
    content: `HARDSCAPE INSTALLATION CONTRACT

This Hardscape Installation Contract ("Agreement") is entered into as of {{date}} by and between:

CONTRACTOR:
{{company_name}}
{{company_address}}
Phone: {{company_phone}} | Email: {{company_email}}
License #: ________________
(hereinafter referred to as "Contractor")

PROPERTY OWNER / CLIENT:
{{client_name}}
{{client_company}}
Email: {{client_email}} | Phone: {{client_phone}}
(hereinafter referred to as "Client")

──────────────────────────────────────────────────

1. PROJECT DESCRIPTION

Property Address: {{project_address}}
Project: {{project_description}}

──────────────────────────────────────────────────

2. SCOPE OF WORK

{{scope_of_work}}

May include:
• Site grading and excavation
• Base material installation and compaction
• Paver, stone, or concrete installation
• Retaining wall construction
• Drainage solutions and French drains
• Outdoor lighting conduit (if specified)
• Final grading and cleanup

──────────────────────────────────────────────────

3. PROJECT SCHEDULE

{{estimated_hours}}

Estimated Start Date: ________________
Estimated Completion: ________________
Duration: ________________

Weather delays extend the timeline accordingly.

──────────────────────────────────────────────────

4. CONTRACT PRICE & PAYMENT

Total Price: \${{estimated_cost}}
Payment Terms: {{payment_terms}}

• 25% deposit upon contract execution
• 25% at material delivery
• 25% at project midpoint
• 25% upon final completion and walkthrough

──────────────────────────────────────────────────

5. WARRANTY

Contractor warrants all workmanship for two (2) years from completion. Manufacturer warranties apply to all pavers, stone, and materials as provided by the manufacturer. Settling, shifting due to tree roots, or improper drainage not caused by Contractor is excluded.

──────────────────────────────────────────────────

6. ADDITIONAL TERMS

{{terms_and_conditions}}

{{proposal_notes}}

──────────────────────────────────────────────────

7. SIGNATURES

CONTRACTOR:
Name: {{company_name}}
Signature: _________________________ Date: __________

CLIENT:
Name: {{client_name}}
Signature: _________________________ Date: __________
`,
  },
  {
    name: "Snow & Ice Removal Agreement",
    description: "Seasonal snow removal contract covering plowing, de-icing, sidewalk clearing, and emergency response for commercial or residential properties.",
    content: `SNOW & ICE REMOVAL AGREEMENT

This Snow & Ice Removal Agreement ("Agreement") is entered into as of {{date}} by and between:

CONTRACTOR:
{{company_name}}
{{company_address}}
Phone: {{company_phone}} | Email: {{company_email}}
(hereinafter referred to as "Contractor")

CLIENT:
{{client_name}}
{{client_company}}
Email: {{client_email}} | Phone: {{client_phone}}
(hereinafter referred to as "Client")

──────────────────────────────────────────────────

1. SERVICE LOCATION

Property Address: {{project_address}}
Total Area: ________________ sq ft
Lot / Parking Area: ________________ sq ft
Sidewalk Length: ________________ linear ft

──────────────────────────────────────────────────

2. SERVICES PROVIDED

{{scope_of_work}}

Standard Services:
• Snow plowing of parking lots and driveways (trigger depth: ___" accumulation)
• Sidewalk and walkway shoveling / snow blowing
• De-icing agent application (salt, calcium chloride, or sand mix)
• Emergency / priority response for hazardous conditions
• Snow stacking / relocation as needed

──────────────────────────────────────────────────

3. SERVICE SEASON & RESPONSE TIME

Season: {{contract_length}}
Response Time: Within _____ hours of snowfall reaching trigger depth
Operating Hours: 24/7 during active snow events

──────────────────────────────────────────────────

4. PRICING

Total Seasonal Contract: \${{estimated_cost}}
Payment Terms: {{payment_terms}}

Pricing Model:
☐ Seasonal flat rate
☐ Per-push pricing: $________________ per visit
☐ Per-inch pricing (tiered)
☐ Time & materials

──────────────────────────────────────────────────

5. LIABILITY & INDEMNIFICATION

Contractor maintains liability insurance for snow operations. Client is responsible for marking obstacles, curbs, and landscaping features prior to first snowfall. Contractor is not liable for damage to unmarked items beneath snow cover.

──────────────────────────────────────────────────

6. ADDITIONAL TERMS

{{terms_and_conditions}}

{{proposal_notes}}

──────────────────────────────────────────────────

7. SIGNATURES

CONTRACTOR:
Name: {{company_name}}
Signature: _________________________ Date: __________

CLIENT:
Name: {{client_name}}
Signature: _________________________ Date: __________
`,
  },
  {
    name: "Year-Round Grounds Management Contract",
    description: "Comprehensive annual grounds management contract covering all four seasons including lawn care, irrigation, seasonal plantings, and winter services.",
    content: `YEAR-ROUND GROUNDS MANAGEMENT CONTRACT

This Grounds Management Contract ("Agreement") is entered into as of {{date}} by and between:

CONTRACTOR:
{{company_name}}
{{company_address}}
Phone: {{company_phone}} | Email: {{company_email}}
(hereinafter referred to as "Contractor")

CLIENT:
{{client_name}}
{{client_company}}
Email: {{client_email}} | Phone: {{client_phone}}
(hereinafter referred to as "Client")

──────────────────────────────────────────────────

1. PROPERTY

Property Address: {{project_address}}
Property Description: {{project_description}}
Total Managed Area: ________________ sq ft

──────────────────────────────────────────────────

2. YEAR-ROUND SCOPE OF SERVICES

{{scope_of_work}}

SPRING (March – May):
• Spring cleanup and debris removal
• Mulch application and bed preparation
• Pre-emergent weed control
• Irrigation system startup and inspection
• First mowing cycle begins

SUMMER (June – August):
• Weekly mowing, edging, and trimming
• Fertilization applications
• Irrigation monitoring and adjustments
• Pest and disease monitoring
• Deadheading and seasonal color maintenance

FALL (September – November):
• Leaf removal and fall cleanup
• Aeration and overseeding
• Fall fertilization
• Irrigation winterization
• Shrub and tree pruning

WINTER (December – February):
• Snow and ice management (if applicable)
• Equipment maintenance and planning
• Annual landscape assessment and recommendations
• Contract renewal planning

──────────────────────────────────────────────────

3. SERVICE FREQUENCY

Growing Season: Weekly / Bi-Weekly
Off-Season: Monthly / As-Needed
Total Estimated Annual Visits: ________________

──────────────────────────────────────────────────

4. ANNUAL CONTRACT PRICE

Total Annual Price: \${{estimated_cost}}
Payment Terms: {{payment_terms}}

Equal Monthly Payments: $________________ / month (12 months)

──────────────────────────────────────────────────

5. TERM & RENEWAL

Initial Term: One (1) year from {{start_date}}
Auto-Renewal: Renews annually unless thirty (30) days' written notice is provided.

──────────────────────────────────────────────────

6. ADDITIONAL TERMS

{{terms_and_conditions}}

{{proposal_notes}}

──────────────────────────────────────────────────

7. SIGNATURES

CONTRACTOR:
Name: {{company_name}}
Signature: _________________________ Date: __________

CLIENT:
Name: {{client_name}}
Signature: _________________________ Date: __________
`,
  },
];

export const LANDSCAPING_TERMINOLOGY = {
  proposalLabel: "Landscaping Proposal",
  contractLabel: "Landscaping Agreement",
  staffLabel: "Crew / Team",
  rateLabel: "Crew Rate",
  industryTerms: [
    "grounds maintenance",
    "hardscape",
    "irrigation",
    "mulching",
    "aeration",
    "overseeding",
    "edging",
    "pruning",
    "seasonal cleanup",
    "turf management",
    "erosion control",
    "retaining wall",
    "paver installation",
    "de-icing",
    "snow plowing",
    "French drain",
    "sod installation",
    "landscape design",
    "bed maintenance",
    "tree service",
  ],
};
